import { formatDate, formatNumber, NgClass, NgStyle } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, LOCALE_ID, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ClassDirective, StyleDirective } from "@ngbracket/ngx-layout/extended";
import { TruncateTextComponent } from "@tylertech/common-components-module";
import { ForgeIconModule, ForgeLabelValueModule } from "@tylertech/forge-angular";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { Align, KeyValue, PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject } from "../../../../interfaces";
import { CachedCodeLookupService, MetadataPropertyService, MetadataService } from "../../../../services";

@Component({
    selector: "metadata-label-value",
    templateUrl: "./metadata-label-value.tmpl.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ClassDirective,
        StyleDirective,
        ForgeIconModule,
        ForgeLabelValueModule,
        NgClass,
        NgStyle,
        TruncateTextComponent,
    ]
})

export class MetadataInputLabelValueComponent implements OnInit, OnChanges
{
    private changeDetectorRef = inject(ChangeDetectorRef);
    private readonly $metadataService = inject(MetadataService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $cachedCodeLookupService = inject(CachedCodeLookupService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    locale = inject(LOCALE_ID);

    @Input()
    model: string;

    @Input()
    formattedModel: string;

    @Input()
    label: string;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    metadataModel: AugmentedModelObject;

    @Input()
    parent: AugmentedModelObject;

    @Input()
    dense?: boolean;

    @Input()
    compact?: boolean;

    @Input()
    isMenu?: boolean;

    value: string;
    loading: boolean = true;
    isRefreshing: boolean = false;

    ngOnInit(): void
    {
        this.setValue();
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (this.loading)
            return;
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                case "formattedModel":
                    this.loading = true;
                    this.setValue();
                    break;
            }
        }
    }

    setValue(): void
    {
        const self = this;
        if (this.propertyMetadata.Lookup)
            this.getValue(this.model, this.propertyMetadata).then(v => self.formatValue(v, true));
        else if (this.propertyMetadata.LookupProperty)
        {
            const list: KeyValue[] = this.$metadataService.DependentModuleValue(this.propertyMetadata.LookupProperty, this.parent, this.metadataModel);
            const v = Utilities.IsNullOrUndefinedOrEmpty(list) ? "" : list.find(x => x.Key === this.model)?.Value;
            this.formatValue(v, true);
        }
        else
            this.formatValue(this.model, false);
    }

    getValue(value: any, property: PropertyMetadata): Promise<string>
    {
        if (Utilities.IsNullOrUndefined(value))
            return Promise.resolve("");
        let fieldValue = "";
        const builder = this.$odataFilterFactory.createBuilder();
        builder.filterEQ("Key", value);
        return this.$cachedCodeLookupService.Get(property.Lookup, builder.build())
            .then(
                x =>
                {
                    if (x && x.length > 0) fieldValue = x[0].Value;
                    return fieldValue;
                },
                () =>
                {
                    return "Error Loading";
                }
            );
    }

    formatValue(v: string, isLookup: boolean): void
    {
        if (isLookup)
        {
            this.value = v;
            this.changeDetectorRef.markForCheck();
        }
        else
        {
            const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,10})?Z?(?:-|\+)?(?:\d{2}:\d{2})?$/;
            if (!!v && v.toString().match(dateRegex))
            {
                let format = "MM/dd/yyyy";
                if (/00:00:00/.test(v))
                    format = "MM/dd/yyyy h:mm a";
                this.value = formatDate(v, format, this.locale);
            }
            else if (!Utilities.IsNullOrUndefined(this.scale))
            {
                this.value = formatNumber(parseFloat(v), this.locale, `1.${this.scale}-${this.scale}`);
            }
            else
            {
                this.value = !Utilities.IsNullOrUndefinedOrEmpty(v) ? this.formattedModel : "n/a";
            }
        }

        this.loading = false;
    }

    get scale(): number
    {
        if (this.propertyMetadata.Scale === 0)
            return 0;
        return this.propertyMetadata.Scale
            ? this.propertyMetadata.Scale
            : !this.propertyMetadata.ScaleProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.ScaleProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    get density(): string
    {
        return this.dense ? "dense" : "";
    }

    get boolean(): boolean
    {
        return this.propertyMetadata["$InputType"] === "Check";
    }

    get showCheck(): boolean
    {
        return this.boolean && this.value === "true";
    }

    get singleLine(): boolean
    {
        return this.$metadataPropertyService.isSingleLine(this.propertyMetadata);
    }

    get alignment(): Record<string, string>
    {
        let align = "left";
        if (this.propertyMetadata.Alignment)
        {
            switch (this.propertyMetadata.Alignment)
            {
                case Align.Right:
                    align = "right";
                    break;
                case Align.Center:
                    align = "center";
                    break;
                default:
                    break;
            }
        }

        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "--forge-label-value-align": align
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    get isInline(): boolean
    {
        return (this.dense || this.compact || this.singleLine) && !this.isMenu;
    }
}
